/**
 * First-touch UTM capture (D-094, 04-architecture § 14).
 *
 * First-party storage only: no cookie, nothing sent to a third party. The first
 * touch wins and is never overwritten; it expires after 90 days.
 *
 * Legal review OI-L-05 covers the `localStorage` half of this. If counsel
 * objects, set `USE_LOCAL_STORAGE = false` — that is the whole change.
 */

const KEY = 'cowoo.ft';
const MAX_AGE_MS = 90 * 24 * 3600_000;
const MAX_PARAM_BYTES = 1024;

/** OI-L-05 — flip to `false` to keep first-touch in the session only. */
export const USE_LOCAL_STORAGE = true;

export interface FirstTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  referrer_host?: string;
  landing_host?: string;
  ts: string;
}

function safeStorage(kind: 'local' | 'session'): Storage | null {
  try {
    const store = kind === 'local' ? globalThis.localStorage : globalThis.sessionStorage;
    return store ?? null;
  } catch {
    // Private mode, blocked site data, SSR: first-touch is a nice-to-have.
    return null;
  }
}

function read(kind: 'local' | 'session'): FirstTouch | null {
  const store = safeStorage(kind);
  if (!store) return null;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FirstTouch;
    if (typeof parsed.ts !== 'string') return null;
    if (Date.now() - Date.parse(parsed.ts) > MAX_AGE_MS) {
      store.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function write(kind: 'local' | 'session', value: FirstTouch): void {
  try {
    safeStorage(kind)?.setItem(KEY, JSON.stringify(value));
  } catch {
    /* storage full or blocked — ignore */
  }
}

function safeHost(url: string): string | null {
  try {
    return url ? new URL(url).host : null;
  } catch {
    return null;
  }
}

/** Reading order: `localStorage` first, then `sessionStorage`. */
export function readFirstTouch(): FirstTouch | null {
  return (USE_LOCAL_STORAGE ? read('local') : null) ?? read('session');
}

/**
 * Called on the first page view of a browser session. Without any `utm_*`
 * parameter only referrer/landing host and the timestamp are stored
 * ("ohne Code = Direkt").
 */
export function captureFirstTouch(
  search: string,
  referrer: string,
  ownHosts: string[],
  now: Date = new Date(),
): FirstTouch | null {
  const existing = readFirstTouch();
  if (existing) return existing;

  const params = new URLSearchParams(search);
  const record: FirstTouch = { ts: now.toISOString() };
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const) {
    const value = params.get(key);
    if (value) record[key] = value.slice(0, 120);
  }

  const referrerHost = safeHost(referrer);
  if (referrerHost && !ownHosts.includes(referrerHost)) record.referrer_host = referrerHost;

  try {
    record.landing_host = globalThis.location?.host;
  } catch {
    /* SSR */
  }

  write('session', record);
  if (USE_LOCAL_STORAGE && !read('local')) write('local', record);
  return record;
}

/** § 14.3 — different origins cannot share storage, so CTAs carry `?ft=`. */
export function encodeFirstTouchParam(value: FirstTouch): string {
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeFirstTouchParam(param: string): FirstTouch | null {
  if (param.length > MAX_PARAM_BYTES) return null;
  try {
    const binary = atob(param.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as FirstTouch;
    return typeof parsed.ts === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/** Appends `?ft=` to a CTA that leaves the current origin. */
export function withFirstTouch(url: string, value: FirstTouch | null = readFirstTouch()): string {
  if (!value) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}ft=${encodeFirstTouchParam(value)}`;
}

/** `member` decodes the parameter on load and removes it from the address bar. */
export function adoptFirstTouchParam(search: string): FirstTouch | null {
  const param = new URLSearchParams(search).get('ft');
  if (!param) return null;
  const decoded = decodeFirstTouchParam(param);
  if (!decoded) return null;
  if (!readFirstTouch()) {
    write('session', decoded);
    if (USE_LOCAL_STORAGE) write('local', decoded);
  }
  return decoded;
}
